import CloseIcon from '@mui/icons-material/Close';
import {Dialog, DialogProps, IconButton} from '@mui/material';
import {useEffect} from 'react';

import {useAnnotatorOverlayProps} from '~components/images/annotator/hooks/useAnnotatorOverlayProps';

export const FULLSCREEN_DIALOG_CLOSE_CLASS = 'hasFullscreenDialogClose';
let fullscreenDialogCloseCount = 0;

export const acquireFullscreenDialogCloseSpace = () => {
  if (typeof document === 'undefined') {
    return;
  }

  fullscreenDialogCloseCount += 1;
  document.body.classList.add(FULLSCREEN_DIALOG_CLOSE_CLASS);
};

export const releaseFullscreenDialogCloseSpace = () => {
  if (typeof document === 'undefined') {
    return;
  }

  fullscreenDialogCloseCount = Math.max(0, fullscreenDialogCloseCount - 1);
  if (fullscreenDialogCloseCount === 0) {
    document.body.classList.remove(FULLSCREEN_DIALOG_CLOSE_CLASS);
  }
};

export interface ClosableDialogProps extends Omit<DialogProps, 'onClose'> {
  fixedToTop?: boolean;
  hasTabs?: boolean;
  hasCloseButton?: boolean;
  onClose?: () => void;
}

export function ClosableDialog({
  fixedToTop,
  hasTabs,
  hasCloseButton = false,
  onClose,
  children,
  open,
  fullScreen = false,
  sx,
  disablePortal,
  ...rest
}: ClosableDialogProps): JSX.Element {
  const overlayProps = useAnnotatorOverlayProps();
  const shouldReserveHeaderSpace = Boolean(open && fullScreen && hasCloseButton);

  useEffect(() => {
    if (!shouldReserveHeaderSpace) {
      return;
    }

    return () => {
      releaseFullscreenDialogCloseSpace();
    };
  }, [shouldReserveHeaderSpace]);

  useEffect(() => {
    if (!shouldReserveHeaderSpace) {
      return;
    }

    acquireFullscreenDialogCloseSpace();
  }, [shouldReserveHeaderSpace]);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={onClose}
      disablePortal={disablePortal}
      {...overlayProps}
      sx={[
        fixedToTop && {
          '& .MuiDialog-container': {
            paddingY: 5,
            alignItems: 'flex-start',
          },
        },
        hasTabs && {
          '& .MuiTabs-root': {
            paddingX: 3,
          },
        },
        {
          '& .MuiDialog-paper': {
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {hasCloseButton && (
        <IconButton
          data-testid='dialog-close-button'
          size='small'
          onClick={onClose}
          sx={(theme) => ({
            position: 'absolute',
            top: theme.spacing(3),
            right: theme.spacing(3),
            color: 'inherit',
            zIndex: 1,
          })}
        >
          <CloseIcon />
        </IconButton>
      )}
      {children}
    </Dialog>
  );
}
